import React, { useRef, useEffect, useState } from 'react';
import './NavMenus.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { setLanguage } from '../i18n';

const SUPPORTED = ['en', 'ro', 'es', 'de'];
const API = 'https://api.prostay.com';

function NavLanguage({ onClose }) {
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [hrefByLang, setHrefByLang] = useState({ en: '#', es: '#', ro: '#', de: '#' });

  const ensureTrailing = (p) => (p === '/' ? '/' : (p.endsWith('/') ? p : p + '/'));

  const stripLang = (pathname) => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length && SUPPORTED.includes(parts[0])) parts.shift();
    const clean = '/' + parts.join('/');
    const hadTrailing = pathname.length > 1 && pathname.endsWith('/');
    return hadTrailing ? ensureTrailing(clean || '/') : (clean || '/');
  };

  const fetchArticleBySlug = async (slug) => {
    try {
      const res = await fetch(`${API}/WebsiteKnowledgebase/get-article-by-slug/${encodeURIComponent(slug)}`);
      if (!res.ok) throw new Error('not ok');
      return await res.json(); // { slug, slugTranslations?: { [lang]: string }, ... }
    } catch (e) {
      console.error('slug fetch failed', e);
      return null;
    }
  };

  // ⭐ Correct slug resolver (fix): English uses article.slug (default)
  const getTargetSlug = (lang, articleData, currentSlug) => {
    if (!articleData) return currentSlug;
    if (lang === 'en') {
      return articleData.slug || articleData.slugTranslations?.en || currentSlug;
    }
    return articleData.slugTranslations?.[lang] || currentSlug;
  };

  const buildHrefFor = (lang, basePath, qs, hash, articleData, context) => {
    let path = basePath;

    if (context?.type === 'blog') {
      const targetSlug = getTargetSlug(lang, articleData, context.currSlug);
      path = `/blog/${targetSlug}${context.pageSeg || ''}/`;
    } else if (context?.type === 'resources') {
      const targetSlug = getTargetSlug(lang, articleData, context.currSlug);
      path = `/resources/${context.cat}/${context.subcat}/${targetSlug}/`;
    } else {
      path = ensureTrailing(basePath);
    }

    const localized = lang === 'en' ? path : `/${lang}${path}`;
    return `${localized}${qs}${hash}`;
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const base = stripLang(location.pathname);
      const qs = location.search || '';
      const hash = location.hash || '';

      let context = { type: null };
      const blogMatch = base.match(/^\/blog\/([^/]+)(?:\/page\/(\d+))?\/?$/i);
      const resMatch = base.match(/^\/resources\/([^/]+)\/([^/]+)\/([^/]+)\/?$/i);

      let articleData = null;

      if (blogMatch) {
        context = { type: 'blog', currSlug: blogMatch[1], pageSeg: blogMatch[2] ? `/page/${blogMatch[2]}` : '' };
        articleData = await fetchArticleBySlug(context.currSlug);
      } else if (resMatch) {
        context = { type: 'resources', cat: resMatch[1], subcat: resMatch[2], currSlug: resMatch[3] };
        articleData = await fetchArticleBySlug(context.currSlug);
      }

      const next = {};
      SUPPORTED.forEach((lang) => {
        next[lang] = buildHrefFor(lang, base, qs, hash, articleData, context);
      });

      if (!cancelled) setHrefByLang(next);
    })();

    return () => { cancelled = true; };
  }, [location.pathname, location.search, location.hash]);

  const handleLanguageChange = async (lang, e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      await setLanguage(lang);
    } finally {
      const dest = hrefByLang[lang] || '#';
      navigate(dest, { replace: true });
      if (onClose) onClose();
    }
  };

  return (
    <div className='nav-menu-popup' ref={menuRef} style={{ width: 120, left: -10 }}>
      <div className='d-flex justify-content-center' style={{ marginTop: -10 }}>
        <svg width="28" height="20" viewBox="0 0 28 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.67777 2.24264C12.0209 -0.100505 15.8199 -0.100505 18.1631 2.24264L25.5981 9.67767C29.3779 13.4575 26.7009 19.9203 21.3554 19.9203H13.9204L6.48538 19.9203C1.13996 19.9203 -1.53705 13.4575 2.24274 9.67767L9.67777 2.24264Z" fill="#fff" />
        </svg>
      </div>
      <div className='nav-content'>
        <div className='nav-grid-1col'>
          <div className='nav-grid-element d-flex align-items-start'>
            <div className='d-flex flex-column align-items-start'>
              <a href={hrefByLang.en} onClick={(e) => handleLanguageChange('en', e)}>English</a>
            </div>
          </div>
          <div className='nav-grid-element d-flex align-items-start'>
            <div className='d-flex flex-column align-items-start'>
              <a href={hrefByLang.es} onClick={(e) => handleLanguageChange('es', e)}>Espanol</a>
            </div>
          </div>
          {/* add RO/DE similarly */}
        </div>
      </div>
    </div>
  );
}

export default NavLanguage;
